import { useTranslation } from 'react-i18next';
import { BaseModal } from '@/components/modal/BaseModal';
import { EFFECT_CATALOG, type EffectType } from '../../engine/effectsManager';
import { useLoopStore } from '../../store/loopStore';
import { useEffectClipDialogStore } from '../../store/effectClipDialogStore';

interface MobileFXSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileFXSheet({ isOpen, onClose }: MobileFXSheetProps) {
  const { t } = useTranslation();
  const hasSelection = useLoopStore((s) =>
    s.selectionStart !== null && s.selectionEnd !== null &&
    Math.abs((s.selectionEnd ?? 0) - (s.selectionStart ?? 0)) > 50
  );

  const handleAddEffect = (effectType: string) => {
    const loop = useLoopStore.getState();
    if (loop.selectionStart === null || loop.selectionEnd === null) return;

    const selStart = Math.min(loop.selectionStart, loop.selectionEnd);
    const selEnd = Math.max(loop.selectionStart, loop.selectionEnd);
    const catalog = EFFECT_CATALOG[effectType as EffectType];

    useEffectClipDialogStore.getState().openForAdd(
      effectType, selStart, selEnd, { ...catalog.defaults },
    );
    onClose();
  };

  // Group effects by category
  const grouped = Object.entries(EFFECT_CATALOG).reduce<Record<string, { key: string; label: string }[]>>(
    (acc, [key, val]) => {
      (acc[val.category] ??= []).push({ key, label: val.label });
      return acc;
    },
    {},
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      mobileVariant="bottomsheet"
      ariaLabel={t('editor.effects', 'Effects')}
    >
      <div className="flex flex-col max-h-[80dvh]">
        {/* Drag handle */}
        <div className="flex justify-center py-2 shrink-0">
          <div className="w-8 h-1 rounded-full bg-white/20" />
        </div>

        <div className="px-4 pb-2 shrink-0">
          <h3 className="text-sm font-semibold text-[var(--user-page-text)]">
            {t('editor.effects', 'Effects')}
          </h3>
          {!hasSelection && (
            <p className="text-xs text-[#8E8E93] mt-1">
              {t('editor.mobile.selectRegion', 'Select a region on the timeline first')}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4 min-h-0">
          {Object.entries(grouped).map(([category, effects]) => (
            <div key={category} className="mb-3">
              <div className="text-[10px] text-[#636366] font-medium uppercase tracking-wider mb-1.5">
                {category}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {effects.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => handleAddEffect(key)}
                    disabled={!hasSelection}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#1A1A1E] text-sm text-[#E5E5EA] hover:bg-[#2C2C2E] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </BaseModal>
  );
}
